import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PASSWORD_MIN_LENGTH, signupSchema } from '@mindpeer/auth-shared';
import { AuthLayout } from '@/components/auth-layout';
import {
  DarkAlert,
  DarkCheckbox,
  DarkField,
  DarkInput,
  DarkSubmit,
  FieldIcons,
  ModuleBanner,
} from '@/components/ui';
import { useAuthRequest } from '@/hooks/use-auth-request';
import { useZodForm } from '@/hooks/use-zod-form';
import { api } from '@/lib/api';

export function SignUpPage() {
  const [params] = useSearchParams();
  const invitationToken = params.get('invitation') ?? undefined;
  /**
   * Prefilled from the invitation. An invitation is only honoured for the
   * address it was sent to, so letting the invitee retype a different one just
   * produces an account that silently fails to join their organization.
   */
  const invitedEmail = params.get('email') ?? '';
  const [sentTo, setSentTo] = useState<string | null>(null);
  const authRequest = useAuthRequest();

  const form = useZodForm({
    schema: signupSchema,
    initialValues: {
      firstName: '',
      lastName: '',
      email: invitedEmail,
      password: '',
      passwordConfirmation: '',
      acceptedTerms: false,
      ...(invitationToken ? { invitationToken } : {}),
    },
    onSubmit: async (input) => {
      await api.signup(input);
      setSentTo(input.email);
    },
  });

  /**
   * The server answers identically whether or not the address was already
   * registered, so this confirmation must not imply a new account was created
   * (PRD §16 — no account enumeration).
   */
  if (sentTo) {
    return (
      <AuthLayout
        title="Check your email"
        description={
          <>
            If an account can be created for <strong>{sentTo}</strong>, a verification link is on
            its way. The link expires after a short time.
          </>
        }
        footer={
          <Link
            to={authRequest.link('/sign-in')}
            className="font-medium text-brand-400 hover:text-brand-300"
          >
            Back to sign in
          </Link>
        }
      >
        <DarkAlert tone="success">
          You can close this page. Open the link from your email to finish setting up your account.
        </DarkAlert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      description="One MindPeer account gives you access to every module your organization uses."
      footer={
        <>
          Already have an account?{' '}
          <Link
            to={authRequest.link('/sign-in')}
            className="font-medium text-brand-400 hover:text-brand-300"
          >
            Sign in
          </Link>
        </>
      }
    >
      <ModuleBanner applicationName={authRequest.applicationName} />

      <form onSubmit={form.handleSubmit} noValidate className="space-y-5">
        {form.formError && <DarkAlert tone="error">{form.formError}</DarkAlert>}

        {invitationToken && (
          <DarkAlert tone="info">
            You&apos;re signing up from an invitation, so you&apos;ll join that organization once
            your email is verified.
            {invitedEmail && (
              <>
                {' '}
                Keep the address as <strong>{invitedEmail}</strong> — the invitation is tied to it.
              </>
            )}
          </DarkAlert>
        )}

        <div className="grid gap-5 sm:grid-cols-2">
          <DarkField
            label="First name"
            htmlFor="field-firstName"
            error={form.errorFor('firstName')}
          >
            <DarkInput
              {...form.fieldProps('firstName')}
              autoComplete="given-name"
              autoFocus
              required
              value={form.values.firstName}
              onChange={(event) => form.setValue('firstName', event.target.value)}
            />
          </DarkField>

          <DarkField label="Last name" htmlFor="field-lastName" error={form.errorFor('lastName')}>
            <DarkInput
              {...form.fieldProps('lastName')}
              autoComplete="family-name"
              required
              value={form.values.lastName}
              onChange={(event) => form.setValue('lastName', event.target.value)}
            />
          </DarkField>
        </div>

        <DarkField
          label="Work email"
          htmlFor="field-email"
          error={form.errorFor('email')}
          hint="Using your work address helps us place you in the right organization."
        >
          <DarkInput
            {...form.fieldProps('email')}
            onBlur={undefined}
            type="email"
            icon={FieldIcons.mail}
            placeholder="you@company.com"
            autoComplete="username"
            required
            value={form.values.email}
            onChange={(event) => form.setValue('email', event.target.value)}
          />
        </DarkField>

        <DarkField
          label="Password"
          htmlFor="field-password"
          error={form.errorFor('password')}
          hint={`At least ${PASSWORD_MIN_LENGTH} characters, including a number or symbol.`}
        >
          <DarkInput
            {...form.fieldProps('password')}
            type="password"
            icon={FieldIcons.lock}
            autoComplete="new-password"
            required
            value={form.values.password}
            onChange={(event) => form.setValue('password', event.target.value)}
          />
        </DarkField>

        <DarkField
          label="Confirm password"
          htmlFor="field-passwordConfirmation"
          error={form.errorFor('passwordConfirmation')}
        >
          <DarkInput
            {...form.fieldProps('passwordConfirmation')}
            type="password"
            icon={FieldIcons.lock}
            autoComplete="new-password"
            required
            value={form.values.passwordConfirmation}
            onChange={(event) => form.setValue('passwordConfirmation', event.target.value)}
          />
        </DarkField>

        <DarkCheckbox
          id="field-acceptedTerms"
          label="I agree to the MindPeer terms of service and privacy policy."
          checked={form.values.acceptedTerms}
          error={form.errorFor('acceptedTerms')}
          onChange={(event) => form.setValue('acceptedTerms', event.target.checked)}
        />

        <DarkSubmit loading={form.submitting}>Create account</DarkSubmit>
      </form>
    </AuthLayout>
  );
}
